import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import GoodsStudioSidebarToolbar from '@/pages/ipe/workbench/components/GoodsStudioSidebarToolbar';
import DetailToolBridgeSourcesPreview from '@/pages/ipe/workbench/components/DetailToolBridgeSourcesPreview';
import {
  getGoodsStudioConfig,
  GOODS_STUDIO_DEFAULT_PARAMS,
} from '@/pages/ipe/workbench/constants/aiStudioConfig';
import '@/styles/ipe/workbench/IpeWorkflowTemplateEditorPage.css';
import '@/styles/ipe/workbench/GoodsStudioPage.css';
import '@/styles/ipe/workbench/Img2ImgPage.css';
import '@/styles/ipe/workbench/modals/GoodsStudioCreateModal.css';

/**
 * 详情页「主图创作」参数弹窗（补充信息 / 国家 / 数量与比例，均为选填）。
 * 商品设计来源：确认后直接创建并提交任务；其它来源确认后带入主图创作页。
 */
export default function GoodsStudioCreateModal({
  isOpen = false,
  onClose,
  onConfirm,
  params = null,
  onParamsChange,
  sources = [],
  sourceCount = 0,
  confirming = false,
  intent = 'create',
}) {
  const { t } = useTranslation('workbench');
  const studioConfig = getGoodsStudioConfig();
  const isRemake = intent === 'remake';
  const [draft, setDraft] = useState(() => ({ ...GOODS_STUDIO_DEFAULT_PARAMS, ...(params || {}) }));
  const [caseOverlayOpen, setCaseOverlayOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setDraft({ ...GOODS_STUDIO_DEFAULT_PARAMS, ...(params || {}) });
    setCaseOverlayOpen(false);
  }, [isOpen, params]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape' && !confirming && !caseOverlayOpen) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, confirming, caseOverlayOpen, onClose]);

  if (!isOpen) return null;

  const previewSources = Array.isArray(sources) && sources.length > 0
    ? sources
    : [];
  const resolvedCount = previewSources.length || Number(sourceCount) || 0;

  const handleChange = (next) => {
    setDraft(next);
    onParamsChange?.(next);
  };

  const handleConfirm = () => {
    onConfirm?.(draft);
  };

  return createPortal(
    (
      <div className="goods-studio-create-modal-layer" role="presentation">
        <div
          className="goods-studio-create-modal-layer__backdrop"
          onClick={() => {
            if (!confirming && !caseOverlayOpen) onClose?.();
          }}
          role="presentation"
        />
        <div
          className="goods-studio-create-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="goods-studio-create-title"
          onClick={(event) => event.stopPropagation()}
        >
          <header className="goods-studio-create-modal__head">
            <div>
              <h2 id="goods-studio-create-title" className="goods-studio-create-modal__title">
                {isRemake ? t('goodsStudioCreate.remakeTitle') : t('goodsStudioCreate.createTitle')}
              </h2>
              <p className="goods-studio-create-modal__subtitle">
                {isRemake
                  ? t('goodsStudioCreate.remakeSubtitle', { count: resolvedCount })
                  : t('goodsStudioCreate.createSubtitle', { count: resolvedCount })}
              </p>
            </div>
            <button
              type="button"
              className="goods-studio-create-modal__close"
              onClick={onClose}
              disabled={confirming}
              aria-label={t('commonActions.close')}
            >
              <X size={18} aria-hidden />
            </button>
          </header>

          <div className="goods-studio-create-modal__body">
            <DetailToolBridgeSourcesPreview
              sources={previewSources}
              title={isRemake ? t('goodsStudioCreate.remakeSources') : t('detailToolBridge.sourcesTitle')}
            />
            <div className="img2img-page goods-studio-page goods-studio-create-modal__params-shell">
              <GoodsStudioSidebarToolbar
                studioConfig={studioConfig}
                params={draft}
                onChange={handleChange}
                disabled={confirming}
                onCaseOverlayChange={setCaseOverlayOpen}
                overlayCentered
              />
            </div>
          </div>

          <footer className="goods-studio-create-modal__foot">
            <button
              type="button"
              className="goods-studio-create-modal__btn goods-studio-create-modal__btn--ghost"
              onClick={onClose}
              disabled={confirming}
            >
              {t('commonActions.cancel')}
            </button>
            <button
              type="button"
              className="goods-studio-create-modal__btn goods-studio-create-modal__btn--primary"
              onClick={handleConfirm}
              disabled={confirming || resolvedCount === 0}
            >
              {confirming
                ? (isRemake ? t('goodsStudioCreate.remakeConfirming') : t('goodsStudioCreate.createConfirming'))
                : (isRemake ? t('goodsStudioCreate.startRemake') : t('goodsStudioCreate.confirmBring'))}
            </button>
          </footer>
        </div>
      </div>
    ),
    document.body,
  );
}
